import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type SMCCComplianceRule, type SMCCKillSwitch, type SMCCBrandVoice } from '../../../services/api';
import { useToast } from '../../../components/common/Toast';
import { useLanguage } from '../../../contexts/LanguageContext';

type Tab = 'brand-voices' | 'compliance' | 'kill-switch' | 'general';
type BrandVoiceTone = 'professional' | 'casual' | 'friendly' | 'playful' | 'formal' | 'inspirational';
type EmojiUsage = 'none' | 'minimal' | 'moderate' | 'frequent';
type ComplianceRuleType = 'keyword_block' | 'hashtag_limit' | 'mention_limit' | 'link_check' | 'image_check' | 'time_restriction' | 'approval_required';
type ComplianceSeverity = 'low' | 'medium' | 'high' | 'critical';
type ViolationAction = 'block' | 'warn' | 'flag_for_review';




const TONE_OPTIONS: { value: BrandVoiceTone; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'playful', label: 'Playful' },
  { value: 'formal', label: 'Formal' },
  { value: 'inspirational', label: 'Inspirational' },
];

const EMOJI_OPTIONS: { value: EmojiUsage; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'frequent', label: 'Frequent' },
];

const RULE_TYPE_OPTIONS: { value: ComplianceRuleType; label: string; description: string }[] = [
  { value: 'keyword_block', label: 'Keyword Block', description: 'Block posts containing specific keywords' },
  { value: 'hashtag_limit', label: 'Hashtag Limit', description: 'Limit number of hashtags' },
  { value: 'mention_limit', label: 'Mention Limit', description: 'Limit number of mentions' },
  { value: 'link_check', label: 'Link Check', description: 'Validate URLs in posts' },
  { value: 'time_restriction', label: 'Time Restriction', description: 'Restrict posting hours' },
  { value: 'approval_required', label: 'Approval Required', description: 'Require approval before posting' },
];

const SEVERITY_OPTIONS: { value: ComplianceSeverity; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'bg-blue-100 text-blue-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
];

const ACTION_OPTIONS: { value: ViolationAction; label: string }[] = [
  { value: 'warn', label: 'Warn' },
  { value: 'flag_for_review', label: 'Flag for Review' },
  { value: 'block', label: 'Block' },
];

const PLATFORMS = ['instagram', 'tiktok', 'twitter', 'facebook', 'pinterest', 'youtube', 'linkedin'];

export default function SMCCSettings() {
  const { t } = useLanguage();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<Tab>('brand-voices');
  const [showBrandVoiceModal, setShowBrandVoiceModal] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [showKillSwitchModal, setShowKillSwitchModal] = useState(false);
  const [editingVoice, setEditingVoice] = useState<SMCCBrandVoice | null>(null);
  const [editingRule, setEditingRule] = useState<SMCCComplianceRule | null>(null);

  // Form states
  const [voiceForm, setVoiceForm] = useState({
    name: '',
    description: '',
    tone: 'professional' as string,
    emoji_usage: 'minimal' as string,
    personality_traits: [] as string[],
    is_default: false,
    is_active: true,
  });

  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    rule_type: 'keyword_block' as ComplianceRuleType,
    config: {} as Record<string, unknown>,
    platforms: [] as string[],
    violation_action: 'warn' as ViolationAction,
    severity: 'medium' as ComplianceSeverity,
    is_active: true,
  });

  const [killSwitchForm, setKillSwitchForm] = useState({
    scope: 'global' as 'global' | 'platform',
    platform: '',
    reason: '',
  });

  const [generalSettings, setGeneralSettings] = useState({
    auto_publish_enabled: false,
    require_approval: true,
    default_automation_level: 'human_approval',
    inbox_sync_interval_minutes: 15,
    max_scheduled_posts_per_day: 10,
    timezone: 'UTC',
  });

  // Queries
  const { data: voicesData, isLoading: voicesLoading } = useQuery({
    queryKey: ['smcc-brand-voices'],
    queryFn: () => api.getSMCCBrandVoices(),
    enabled: activeTab === 'brand-voices',
  });

  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['smcc-compliance-rules'],
    queryFn: () => api.getSMCCComplianceRules(),
    enabled: activeTab === 'compliance',
  });

  const { data: killSwitchData, isLoading: killSwitchLoading } = useQuery({
    queryKey: ['smcc-kill-switch'],
    queryFn: () => api.getSMCCKillSwitch(),
    enabled: activeTab === 'kill-switch',
  });

  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['smcc-general-settings'],
    queryFn: () => api.getSMCCGeneralSettings(),
    enabled: activeTab === 'general',
  });

  // Update general settings when data is fetched
  useEffect(() => {
    if (settingsData?.settings) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Sync fetched settings to form state
      setGeneralSettings(prev => ({ ...prev, ...settingsData.settings }));
    }
  }, [settingsData]);

  // Mutations
  const createVoiceMutation = useMutation({
    mutationFn: (data: typeof voiceForm) => api.createSMCCBrandVoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-brand-voices'] });
      setShowBrandVoiceModal(false);
      resetVoiceForm();
      addToast('success', 'Brand voice created');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const updateVoiceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof voiceForm }) => api.updateSMCCBrandVoice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-brand-voices'] });
      setShowBrandVoiceModal(false);
      setEditingVoice(null);
      resetVoiceForm();
      addToast('success', 'Brand voice updated');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const deleteVoiceMutation = useMutation({
    mutationFn: (id: string) => api.deleteSMCCBrandVoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-brand-voices'] });
      addToast('success', 'Brand voice deleted');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const createRuleMutation = useMutation({
    mutationFn: (data: typeof ruleForm) => api.createSMCCComplianceRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-compliance-rules'] });
      setShowComplianceModal(false);
      resetRuleForm();
      addToast('success', 'Compliance rule created');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const updateRuleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof ruleForm }) => api.updateSMCCComplianceRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-compliance-rules'] });
      setShowComplianceModal(false);
      setEditingRule(null);
      resetRuleForm();
      addToast('success', 'Compliance rule updated');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const deleteRuleMutation = useMutation({
    mutationFn: (id: string) => api.deleteSMCCComplianceRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-compliance-rules'] });
      addToast('success', 'Compliance rule deleted');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const activateKillSwitchMutation = useMutation({
    mutationFn: (data: typeof killSwitchForm) => api.activateSMCCKillSwitch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-kill-switch'] });
      setShowKillSwitchModal(false);
      setKillSwitchForm({ scope: 'global', platform: '', reason: '' });
      addToast('success', 'Kill switch activated');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const deactivateKillSwitchMutation = useMutation({
    mutationFn: (id: string) => api.deactivateSMCCKillSwitch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-kill-switch'] });
      addToast('success', 'Kill switch deactivated');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (data: typeof generalSettings) => api.updateSMCCGeneralSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-general-settings'] });
      addToast('success', 'Settings saved');
    },
    onError: (error: Error) => addToast('error', error.message),
  });

  const resetVoiceForm = () => {
    setVoiceForm({
      name: '',
      description: '',
      tone: 'professional',
      emoji_usage: 'minimal',
      personality_traits: [],
      is_default: false,
      is_active: true,
    });
  };

  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      description: '',
      rule_type: 'keyword_block',
      config: {},
      platforms: [],
      violation_action: 'warn',
      severity: 'medium',
      is_active: true,
    });
  };

  const openEditVoice = (voice: SMCCBrandVoice) => {
    setEditingVoice(voice);
    setVoiceForm({
      name: voice.name,
      description: voice.description || '',
      tone: voice.tone,
      emoji_usage: voice.emoji_usage,
      personality_traits: voice.personality_traits || [],
      is_default: voice.is_default,
      is_active: voice.is_active,
    });
    setShowBrandVoiceModal(true);
  };

  const openEditRule = (rule: SMCCComplianceRule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      rule_type: rule.rule_type as ComplianceRuleType,
      config: rule.config || {},
      platforms: rule.platforms || [],
      violation_action: rule.violation_action as ViolationAction,
      severity: rule.severity as ComplianceSeverity,
      is_active: rule.is_active,
    });
    setShowComplianceModal(true);
  };

  const handleSaveVoice = () => {
    if (editingVoice) {
      updateVoiceMutation.mutate({ id: editingVoice.id, data: voiceForm });
    } else {
      createVoiceMutation.mutate(voiceForm);
    }
  };

  const handleSaveRule = () => {
    if (editingRule) {
      updateRuleMutation.mutate({ id: editingRule.id, data: ruleForm });
    } else {
      createRuleMutation.mutate(ruleForm);
    }
  };

  const voices = voicesData?.brandVoices || [];
  const rules = rulesData?.rules || [];
  const activeKillSwitches = killSwitchData?.active_switches || [];
  const globalKillSwitchActive = killSwitchData?.global_active || false;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.smcc.settings.title', 'SMCC Settings')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure brand voices, compliance rules, and system settings
        </p>
      </div>

      {/* Kill Switch Alert */}
      {globalKillSwitchActive && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Global Kill Switch Active</h3>
              <p className="text-sm text-red-700">All automated publishing is currently disabled.</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'brand-voices', label: 'Brand Voices' },
            { id: 'compliance', label: 'Compliance Rules' },
            { id: 'kill-switch', label: 'Kill Switch' },
            { id: 'general', label: 'General' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Brand Voices Tab */}
      {activeTab === 'brand-voices' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">
              Define brand voice profiles for consistent AI-generated content
            </p>
            <button
              onClick={() => {
                resetVoiceForm();
                setEditingVoice(null);
                setShowBrandVoiceModal(true);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              Add Brand Voice
            </button>
          </div>

          {voicesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : voices.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">No brand voices configured</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {voices.map((voice: SMCCBrandVoice) => (
                <div
                  key={voice.id}
                  className={`bg-white rounded-lg border p-4 ${
                    voice.is_default ? 'border-primary-300 ring-1 ring-primary-100' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-gray-900">{voice.name}</h3>
                      {voice.is_default && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                          Default
                        </span>
                      )}
                      {!voice.is_active && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditVoice(voice)}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Edit
                      </button>
                      {!voice.is_default && (
                        <button
                          onClick={() => {
                            if (confirm('Delete this brand voice?')) {
                              deleteVoiceMutation.mutate(voice.id);
                            }
                          }}
                          className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  {voice.description && (
                    <p className="mt-2 text-sm text-gray-600">{voice.description}</p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded capitalize">
                      {voice.tone}
                    </span>
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                      Emoji: {voice.emoji_usage}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compliance Rules Tab */}
      {activeTab === 'compliance' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">
              Define rules to ensure content compliance before publishing
            </p>
            <button
              onClick={() => {
                resetRuleForm();
                setEditingRule(null);
                setShowComplianceModal(true);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              Add Rule
            </button>
          </div>

          {rulesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">No compliance rules configured</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="w-24 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rules.map((rule: SMCCComplianceRule) => {
                    const severityOption = SEVERITY_OPTIONS.find(s => s.value === rule.severity);
                    return (
                      <tr key={rule.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{rule.name}</p>
                            {rule.description && (
                              <p className="text-xs text-gray-500">{rule.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                          {rule.rule_type.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${severityOption?.color}`}>
                            {rule.severity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                          {rule.violation_action.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openEditRule(rule)}
                            className="text-gray-400 hover:text-gray-600 mr-2"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Delete this rule?')) {
                                deleteRuleMutation.mutate(rule.id);
                              }
                            }}
                            className="text-red-400 hover:text-red-600"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Kill Switch Tab */}
      {activeTab === 'kill-switch' && (
        <div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Emergency Controls</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Use the kill switch to immediately stop all automated publishing. This is useful during PR crises, technical issues, or other emergencies.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Active Kill Switches</h3>
            <button
              onClick={() => setShowKillSwitchModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
            >
              Activate Kill Switch
            </button>
          </div>

          {killSwitchLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : activeKillSwitches.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">All Systems Operational</h3>
              <p className="mt-1 text-sm text-gray-500">No kill switches are currently active</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeKillSwitches.map((ks: SMCCKillSwitch) => (
                <div key={ks.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 capitalize">
                          {ks.scope}
                        </span>
                        {ks.platform && (
                          <span className="text-sm text-red-700 capitalize">{ks.platform}</span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-red-800">{ks.reason}</p>
                      <p className="mt-1 text-xs text-red-600">
                        Activated {ks.activated_at ? new Date(ks.activated_at).toLocaleString() : 'Unknown'}
                      </p>
                    </div>
                    <button
                      onClick={() => deactivateKillSwitchMutation.mutate(ks.id)}
                      className="px-4 py-2 bg-white border border-red-300 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      Deactivate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {settingsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="flex items-center gap-3" aria-label="Enable Auto-Publishing">
                    <input
                      type="checkbox"
                      checked={generalSettings.auto_publish_enabled}
                      onChange={(e) => setGeneralSettings(prev => ({
                        ...prev,
                        auto_publish_enabled: e.target.checked,
                      }))}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Enable Auto-Publishing</span>
                      <p className="text-xs text-gray-500">Allow fully automated content to be published without manual approval</p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="flex items-center gap-3" aria-label="Require Approval">
                    <input
                      type="checkbox"
                      checked={generalSettings.require_approval}
                      onChange={(e) => setGeneralSettings(prev => ({
                        ...prev,
                        require_approval: e.target.checked,
                      }))}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Require Approval</span>
                      <p className="text-xs text-gray-500">All content must be approved before publishing</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="default-automation-level" className="block text-sm font-medium text-gray-700 mb-1">
                      Default Automation Level
                    </label>
                    <select
                      id="default-automation-level"
                      value={generalSettings.default_automation_level}
                      onChange={(e) => setGeneralSettings(prev => ({
                        ...prev,
                        default_automation_level: e.target.value,
                      }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="manual">Manual</option>
                      <option value="human_approval">Human Approval Required</option>
                      <option value="fully_automated">Fully Automated</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Inbox Sync Interval (minutes)
                      <select
                        value={generalSettings.inbox_sync_interval_minutes}
                        onChange={(e) => setGeneralSettings(prev => ({
                          ...prev,
                          inbox_sync_interval_minutes: parseInt(e.target.value),
                        }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mt-1 block"
                      >
                        <option value="5">5 minutes</option>
                        <option value="15">15 minutes</option>
                        <option value="30">30 minutes</option>
                        <option value="60">1 hour</option>
                      </select>
                    </label>
                  </div>

                  <div>
                    <label htmlFor="max-scheduled-posts" className="block text-sm font-medium text-gray-700 mb-1">
                      Max Scheduled Posts Per Day
                    </label>
                    <input
                      id="max-scheduled-posts"
                      type="number"
                      min="1"
                      max="50"
                      value={generalSettings.max_scheduled_posts_per_day}
                      onChange={(e) => setGeneralSettings(prev => ({
                        ...prev,
                        max_scheduled_posts_per_day: parseInt(e.target.value),
                      }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="settings-timezone" className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone
                    </label>
                    <select
                      id="settings-timezone"
                      value={generalSettings.timezone}
                      onChange={(e) => setGeneralSettings(prev => ({
                        ...prev,
                        timezone: e.target.value,
                      }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                      <option value="Asia/Tokyo">Tokyo</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => updateSettingsMutation.mutate(generalSettings)}
                  disabled={updateSettingsMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Brand Voice Modal */}
      {showBrandVoiceModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowBrandVoiceModal(false)}
              onKeyDown={(e) => e.key === 'Escape' && setShowBrandVoiceModal(false)}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingVoice ? 'Edit Brand Voice' : 'Add Brand Voice'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="voice-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    id="voice-name"
                    type="text"
                    value={voiceForm.name}
                    onChange={(e) => setVoiceForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g., Rumio Main Voice"
                  />
                </div>

                <div>
                  <label htmlFor="voice-description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    id="voice-description"
                    value={voiceForm.description}
                    onChange={(e) => setVoiceForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    rows={2}
                    placeholder="Describe when to use this voice..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="voice-tone" className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
                    <select
                      id="voice-tone"
                      value={voiceForm.tone}
                      onChange={(e) => setVoiceForm(prev => ({ ...prev, tone: e.target.value as BrandVoiceTone }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      {TONE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="voice-emoji-usage" className="block text-sm font-medium text-gray-700 mb-1">Emoji Usage</label>
                    <select
                      id="voice-emoji-usage"
                      value={voiceForm.emoji_usage}
                      onChange={(e) => setVoiceForm(prev => ({ ...prev, emoji_usage: e.target.value as EmojiUsage }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      {EMOJI_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={voiceForm.is_default}
                      onChange={(e) => setVoiceForm(prev => ({ ...prev, is_default: e.target.checked }))}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Set as default</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={voiceForm.is_active}
                      onChange={(e) => setVoiceForm(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowBrandVoiceModal(false);
                    setEditingVoice(null);
                    resetVoiceForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveVoice}
                  disabled={!voiceForm.name}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {editingVoice ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Rule Modal */}
      {showComplianceModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowComplianceModal(false)}
              onKeyDown={(e) => e.key === 'Escape' && setShowComplianceModal(false)}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingRule ? 'Edit Compliance Rule' : 'Add Compliance Rule'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="rule-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    id="rule-name"
                    type="text"
                    value={ruleForm.name}
                    onChange={(e) => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="rule-type" className="block text-sm font-medium text-gray-700 mb-1">Rule Type</label>
                  <select
                    id="rule-type"
                    value={ruleForm.rule_type}
                    onChange={(e) => setRuleForm(prev => ({ ...prev, rule_type: e.target.value as ComplianceRuleType }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {RULE_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="rule-severity" className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                    <select
                      id="rule-severity"
                      value={ruleForm.severity}
                      onChange={(e) => setRuleForm(prev => ({ ...prev, severity: e.target.value as ComplianceSeverity }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      {SEVERITY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="rule-action" className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                    <select
                      id="rule-action"
                      value={ruleForm.violation_action}
                      onChange={(e) => setRuleForm(prev => ({ ...prev, violation_action: e.target.value as ViolationAction }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      {ACTION_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">Platforms</span>
                  <div className="flex flex-wrap gap-2">
                    {PLATFORMS.map(platform => (
                      <label key={platform} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={ruleForm.platforms.includes(platform)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRuleForm(prev => ({ ...prev, platforms: [...prev.platforms, platform] }));
                            } else {
                              setRuleForm(prev => ({ ...prev, platforms: prev.platforms.filter(p => p !== platform) }));
                            }
                          }}
                          className="w-3 h-3 text-primary-600 border-gray-300 rounded"
                        />
                        <span className="text-xs text-gray-700 capitalize">{platform}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ruleForm.is_active}
                    onChange={(e) => setRuleForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowComplianceModal(false);
                    setEditingRule(null);
                    resetRuleForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRule}
                  disabled={!ruleForm.name}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {editingRule ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kill Switch Modal */}
      {showKillSwitchModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowKillSwitchModal(false)}
              onKeyDown={(e) => e.key === 'Escape' && setShowKillSwitchModal(false)}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Activate Kill Switch</h3>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">
                  This will immediately stop all automated publishing for the selected scope.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="killswitch-scope" className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                  <select
                    id="killswitch-scope"
                    value={killSwitchForm.scope}
                    onChange={(e) => setKillSwitchForm(prev => ({
                      ...prev,
                      scope: e.target.value as 'global' | 'platform',
                      platform: e.target.value === 'global' ? '' : prev.platform,
                    }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="global">Global (All Platforms)</option>
                    <option value="platform">Specific Platform</option>
                  </select>
                </div>

                {killSwitchForm.scope === 'platform' && (
                  <div>
                    <label htmlFor="killswitch-platform" className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                    <select
                      id="killswitch-platform"
                      value={killSwitchForm.platform}
                      onChange={(e) => setKillSwitchForm(prev => ({ ...prev, platform: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Select platform...</option>
                      {PLATFORMS.map(platform => (
                        <option key={platform} value={platform} className="capitalize">{platform}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label htmlFor="killswitch-reason" className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <textarea
                    id="killswitch-reason"
                    value={killSwitchForm.reason}
                    onChange={(e) => setKillSwitchForm(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    rows={3}
                    placeholder="Describe why the kill switch is being activated..."
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setShowKillSwitchModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => activateKillSwitchMutation.mutate(killSwitchForm)}
                  disabled={!killSwitchForm.reason || (killSwitchForm.scope === 'platform' && !killSwitchForm.platform)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  Activate Kill Switch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
